import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Clock, Loader2 } from 'lucide-react';
import { Seo, SITE_URL } from '@/lib/seo';
import { API, abs, formatDate } from '@/lib/mediaUrl';
import { StoryCover } from '@/components/StoryCover';

const Stories = () => {
    const [posts, setPosts] = useState(null);

    useEffect(() => {
        let alive = true;
        fetch(`${API}/posts`)
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => alive && setPosts(Array.isArray(d) ? d : []))
            .catch(() => alive && setPosts([]));
        return () => {
            alive = false;
        };
    }, []);

    return (
        <>
            <Seo
                title="Stories · Himanshu Tanty"
                description="Notes, lessons and behind-the-scenes writing on product design, UX and building useful software by Himanshu Tanty."
                canonical={`${SITE_URL}/stories`}
            />
            <section
                data-testid="stories-page"
                className="relative px-4 md:px-6 pt-24 md:pt-28 pb-16 md:pb-24"
            >
                <div className="mx-auto max-w-[1100px]">
                    <div className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                        <span className="w-6 h-[1px] bg-[var(--line-2)]" />
                        <span>Stories</span>
                    </div>
                    <h1 className="mt-4 font-display leading-[0.95] text-4xl md:text-6xl text-[var(--ink)]">
                        Notes from the{' '}
                        <span className="font-display-italic text-[var(--accent)]">
                            studio
                        </span>
                        <span className="text-[var(--ink-2)]">.</span>
                    </h1>
                    <p className="mt-4 font-sans-brand text-[14px] leading-[1.6] text-[var(--ink-2)] max-w-[60ch]">
                        Occasional writing on design, process and the craft of
                        making complex things feel simple.
                    </p>

                    {posts === null ? (
                        <div className="py-24 flex items-center justify-center text-[var(--ink-3)]">
                            <Loader2 size={22} className="animate-spin" />
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="mt-12 py-16 rounded-[22px] border border-dashed border-[var(--line)] bg-[var(--surface)] text-center">
                            <div className="font-display-italic text-3xl text-[var(--ink-2)]">
                                No stories yet.
                            </div>
                            <div className="mt-2 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                Check back soon
                            </div>
                        </div>
                    ) : (
                        <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {posts.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-8%' }}
                                    transition={{ duration: 0.55, delay: (i % 3) * 0.06 }}
                                >
                                    <Link
                                        to={`/stories/${p.slug}`}
                                        data-testid={`story-card-${p.slug}`}
                                        data-cursor="hover"
                                        className="group block h-full rounded-2xl border border-[var(--line)] bg-[var(--surface)] overflow-hidden hover:border-[var(--line-2)] transition-colors"
                                    >
                                        <div className="relative aspect-[16/10] overflow-hidden bg-[var(--surface-2)]">
                                            {p.pinned && (
                                                <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur font-mono-brand text-[9px] uppercase tracking-[0.16em] text-[var(--accent)]">
                                                    ★ Pinned
                                                </span>
                                            )}
                                            <StoryCover
                                                post={p}
                                                imgClassName="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-5 md:p-6 flex flex-col gap-3">
                                            <div className="flex items-center gap-3 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                                                <span>{formatDate(p.published_at)}</span>
                                                <span className="w-1 h-1 rounded-full bg-[var(--line-3,#3a3a3d)]" />
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock size={11} /> {p.read_minutes} min
                                                </span>
                                            </div>
                                            <div className="font-display text-xl md:text-2xl leading-tight text-[var(--ink)]">
                                                {p.title}
                                            </div>
                                            {p.excerpt && (
                                                <p className="font-sans-brand text-[13px] leading-[1.55] text-[var(--ink-2)] line-clamp-3">
                                                    {p.excerpt}
                                                </p>
                                            )}
                                            <span className="mt-1 inline-flex items-center gap-1 font-mono-brand text-[10.5px] uppercase tracking-[0.2em] text-[var(--accent)]">
                                                Read
                                                <ArrowUpRight
                                                    size={13}
                                                    className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                                                />
                                            </span>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default Stories;
